import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabaseClient'

// ── Types ──────────────────────────────────────────────

type Partner = {
  id: string
  name: string
  address: string | null
  logo_url: string | null
  website_url: string | null
  contact_name: string
  contact_email: string
  contact_phone: string | null
  status: 'ACTIVE' | 'DISABLED'
  color_code: string | null
  created_at: string
  updated_at: string
}

type PartnerFormData = {
  name: string
  address: string
  contact_name: string
  contact_email: string
  contact_phone: string
  website_url: string
  logo_url: string
  color_code: string
  status: 'ACTIVE' | 'DISABLED'
}

const EMPTY_FORM: PartnerFormData = {
  name: '',
  address: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  website_url: '',
  logo_url: '',
  color_code: '#3b82f6',
  status: 'ACTIVE',
}

// ── Helpers ────────────────────────────────────────────

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })

const validateForm = (form: PartnerFormData): string | null => {
  if (!form.name.trim()) return 'Le nom est obligatoire.'
  if (!form.contact_name.trim()) return 'Le nom du contact est obligatoire.'
  if (!form.contact_email.trim()) return "L'email du contact est obligatoire."
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email))
    return "L'email du contact n'est pas valide."
  return null
}

// ── Styles ─────────────────────────────────────────────

const styles = {
  card: {
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '0.75rem',
    padding: '2rem',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.9rem',
  } as React.CSSProperties,
  th: {
    textAlign: 'left' as const,
    padding: '0.75rem 0.5rem',
    borderBottom: '2px solid #e2e8f0',
    color: '#64748b',
    fontWeight: '600',
    fontSize: '0.85rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  } as React.CSSProperties,
  td: {
    padding: '0.75rem 0.5rem',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155',
  } as React.CSSProperties,
  btnPrimary: {
    padding: '0.5rem 1rem',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  btnSecondary: {
    padding: '0.4rem 0.75rem',
    background: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  } as React.CSSProperties,
  btnDanger: {
    padding: '0.4rem 0.75rem',
    background: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  } as React.CSSProperties,
  btnSuccess: {
    padding: '0.4rem 0.75rem',
    background: '#f0fdf4',
    color: '#16a34a',
    border: '1px solid #bbf7d0',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  } as React.CSSProperties,
  badge: (active: boolean): React.CSSProperties => ({
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '9999px',
    fontSize: '0.8rem',
    fontWeight: '500',
    background: active ? '#dcfce7' : '#fee2e2',
    color: active ? '#166534' : '#991b1b',
  }),
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  } as React.CSSProperties,
  modal: {
    background: 'white',
    borderRadius: '0.75rem',
    padding: '2rem',
    width: '100%',
    maxWidth: '560px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  } as React.CSSProperties,
  formGroup: { marginBottom: '1rem' } as React.CSSProperties,
  label: {
    display: 'block',
    marginBottom: '0.3rem',
    fontWeight: '500',
    fontSize: '0.9rem',
    color: '#334155',
  } as React.CSSProperties,
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,
  select: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
    background: 'white',
  } as React.CSSProperties,
  colorSwatch: (color: string): React.CSSProperties => ({
    display: 'inline-block',
    width: '1rem',
    height: '1rem',
    borderRadius: '50%',
    background: color || '#ccc',
    border: '1px solid #e2e8f0',
    verticalAlign: 'middle',
    marginRight: '0.5rem',
  }),
}

// ── Modal Component ────────────────────────────────────

type ModalProps = {
  title: string
  form: PartnerFormData
  onChange: (form: PartnerFormData) => void
  onSave: () => void
  onClose: () => void
  saving: boolean
  error: string | null
}

const PartnerModal: React.FC<ModalProps> = ({ title, form, onChange, onSave, onClose, saving, error }) => (
  <div style={styles.overlay} onClick={onClose} data-testid="partner-modal">
    <div style={styles.modal} onClick={e => e.stopPropagation()}>
      <h2 style={{ marginTop: 0, marginBottom: '1.5rem' }}>{title}</h2>

      {error && (
        <div
          style={{ padding: '0.75rem', background: '#fef2f2', color: '#dc2626', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.9rem' }}
          data-testid="modal-error"
        >
          {error}
        </div>
      )}

      <div style={styles.formGroup}>
        <label style={styles.label}>Nom du partenaire *</label>
        <input
          style={styles.input}
          data-testid="partner-name-input"
          value={form.name}
          onChange={e => onChange({ ...form, name: e.target.value })}
          placeholder="Ex: Club de Tennis Vaudois"
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Adresse</label>
        <input
          style={styles.input}
          data-testid="partner-address-input"
          value={form.address}
          onChange={e => onChange({ ...form, address: e.target.value })}
          placeholder="Ex: Route de la Vallée 42, 1010 Lausanne"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Nom du contact *</label>
          <input
            style={styles.input}
            data-testid="partner-contact-name-input"
            value={form.contact_name}
            onChange={e => onChange({ ...form, contact_name: e.target.value })}
            placeholder="Jean Dupont"
          />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Email du contact *</label>
          <input
            style={styles.input}
            data-testid="partner-contact-email-input"
            type="email"
            value={form.contact_email}
            onChange={e => onChange({ ...form, contact_email: e.target.value })}
            placeholder="contact@club.ch"
          />
        </div>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Téléphone</label>
        <input
          style={styles.input}
          data-testid="partner-contact-phone-input"
          value={form.contact_phone}
          onChange={e => onChange({ ...form, contact_phone: e.target.value })}
          placeholder="+41 21 123 45 67"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Site web</label>
          <input
            style={styles.input}
            data-testid="partner-website-input"
            value={form.website_url}
            onChange={e => onChange({ ...form, website_url: e.target.value })}
            placeholder="https://www.club.ch"
          />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Couleur</label>
          <input
            type="color"
            style={{ ...styles.input, height: '2.5rem', padding: '0.25rem' }}
            data-testid="partner-color-input"
            value={form.color_code}
            onChange={e => onChange({ ...form, color_code: e.target.value })}
          />
        </div>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Logo URL</label>
        <input
          style={styles.input}
          data-testid="partner-logo-input"
          value={form.logo_url}
          onChange={e => onChange({ ...form, logo_url: e.target.value })}
          placeholder="https://..."
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Statut</label>
        <select
          style={styles.select}
          data-testid="partner-status-select"
          value={form.status}
          onChange={e => onChange({ ...form, status: e.target.value as 'ACTIVE' | 'DISABLED' })}
        >
          <option value="ACTIVE">Actif</option>
          <option value="DISABLED">Désactivé</option>
        </select>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
        <button style={styles.btnSecondary} onClick={onClose} data-testid="modal-cancel-btn">
          Annuler
        </button>
        <button style={styles.btnPrimary} onClick={onSave} disabled={saving} data-testid="modal-save-btn">
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </div>
  </div>
)

// ── Main Page ──────────────────────────────────────────

const AdminPartnersPage: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Modal state
  const [showModal, setShowModal] = useState(false)
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null)
  const [form, setForm] = useState<PartnerFormData>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  // Members count per partner
  const [memberCounts, setMemberCounts] = useState<Record<string, number>>({})
  const [couponCounts, setCouponCounts] = useState<Record<string, number>>({})

  const load = async () => {
    setLoading(true)
    setError(null)

    const [{ data: partnersData, error: pErr }, { data: profilesData, error: prErr }, { data: couponsData, error: cErr }] =
      await Promise.all([
        supabase.from('partners').select('*').order('name'),
        supabase.from('profiles').select('partner_id').not('partner_id', 'is', null),
        supabase.from('coupons').select('partner_id'),
      ])

    if (pErr) {
      setError('Erreur chargement des partenaires.')
      console.error(pErr)
      setLoading(false)
      return
    }

    setPartners(partnersData ?? [])

    // Count members per partner
    const mCounts: Record<string, number> = {}
    for (const p of profilesData ?? []) {
      const pid = (p as any).partner_id as string
      mCounts[pid] = (mCounts[pid] || 0) + 1
    }
    setMemberCounts(mCounts)

    // Count coupons per partner
    const cCounts: Record<string, number> = {}
    for (const c of couponsData ?? []) {
      const pid = (c as any).partner_id as string
      cCounts[pid] = (cCounts[pid] || 0) + 1
    }
    setCouponCounts(cCounts)

    setLoading(false)
  }

  useEffect(() => {
    void load()
  }, [])

  // ── Create / Edit ──

  const openCreate = () => {
    setEditingPartner(null)
    setForm(EMPTY_FORM)
    setFormError(null)
    setShowModal(true)
  }

  const openEdit = (partner: Partner) => {
    setEditingPartner(partner)
    setForm({
      name: partner.name,
      address: partner.address ?? '',
      contact_name: partner.contact_name,
      contact_email: partner.contact_email,
      contact_phone: partner.contact_phone ?? '',
      website_url: partner.website_url ?? '',
      logo_url: partner.logo_url ?? '',
      color_code: partner.color_code ?? '#3b82f6',
      status: partner.status,
    })
    setFormError(null)
    setShowModal(true)
  }

  const handleSave = async () => {
    const err = validateForm(form)
    if (err) {
      setFormError(err)
      return
    }

    setSaving(true)
    setFormError(null)

    const payload = {
      name: form.name.trim(),
      address: form.address.trim() || null,
      contact_name: form.contact_name.trim(),
      contact_email: form.contact_email.trim().toLowerCase(),
      contact_phone: form.contact_phone.trim() || null,
      website_url: form.website_url.trim() || null,
      logo_url: form.logo_url.trim() || null,
      color_code: form.color_code || null,
      status: form.status,
    }

    if (editingPartner) {
      const { error: uErr } = await supabase
        .from('partners')
        .update({ ...payload, updated_at: new Date().toISOString() })
        .eq('id', editingPartner.id)

      if (uErr) {
        setFormError(`Erreur: ${uErr.message}`)
        setSaving(false)
        return
      }
    } else {
      const { error: iErr } = await supabase.from('partners').insert(payload)
      if (iErr) {
        setFormError(`Erreur: ${iErr.message}`)
        setSaving(false)
        return
      }
    }

    setSaving(false)
    setShowModal(false)
    void load()
  }

  // ── Toggle status ──

  const handleToggleStatus = async (partner: Partner) => {
    const newStatus = partner.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE'
    const { error: uErr } = await supabase
      .from('partners')
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', partner.id)

    if (uErr) {
      alert(`Erreur: ${uErr.message}`)
      return
    }
    void load()
  }

  // ── Delete ──

  const handleDelete = async (partner: Partner) => {
    const members = memberCounts[partner.id] ?? 0
    const coupons = couponCounts[partner.id] ?? 0

    if (members > 0 || coupons > 0) {
      alert(
        `Impossible de supprimer "${partner.name}" : il possède ${members} membre(s) et ${coupons} coupon(s).`
      )
      return
    }

    const ok = window.confirm(
      `Supprimer définitivement le partenaire "${partner.name}" ?\n\nCette action est irréversible.`
    )
    if (!ok) return

    const { error: dErr } = await supabase.from('partners').delete().eq('id', partner.id)
    if (dErr) {
      alert(`Erreur: ${dErr.message}`)
      return
    }
    void load()
  }

  // ── Render ──

  if (loading) {
    return (
      <div style={styles.card}>
        <p>Chargement des partenaires…</p>
      </div>
    )
  }

  return (
    <div data-testid="admin-partners-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ margin: 0 }}>Partenaires</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#64748b', fontSize: '0.9rem' }}>
            {partners.length} partenaire{partners.length !== 1 ? 's' : ''} enregistré{partners.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button style={styles.btnPrimary} onClick={openCreate} data-testid="create-partner-btn">
          + Nouveau partenaire
        </button>
      </div>

      {error && (
        <div
          style={{ padding: '0.75rem', background: '#fef2f2', color: '#dc2626', borderRadius: '0.5rem', marginBottom: '1rem' }}
          data-testid="page-error"
        >
          {error}
        </div>
      )}

      <div style={styles.card}>
        {partners.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#94a3b8' }} data-testid="no-partners">
            Aucun partenaire enregistré.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table} data-testid="partners-table">
              <thead>
                <tr>
                  <th style={styles.th}>Partenaire</th>
                  <th style={styles.th}>Contact</th>
                  <th style={styles.th}>Membres</th>
                  <th style={styles.th}>Coupons</th>
                  <th style={styles.th}>Statut</th>
                  <th style={styles.th}>Créé le</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {partners.map(partner => (
                  <tr key={partner.id} data-testid={`partner-row-${partner.id}`}>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={styles.colorSwatch(partner.color_code ?? '#ccc')} />
                        <Link
                          to={`/admin/partners/${partner.id}`}
                          style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}
                          data-testid={`partner-link-${partner.id}`}
                        >
                          {partner.name}
                        </Link>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontSize: '0.85rem' }}>
                        {partner.contact_name}
                        <br />
                        <span style={{ color: '#94a3b8' }}>{partner.contact_email}</span>
                      </div>
                    </td>
                    <td style={styles.td}>{memberCounts[partner.id] ?? 0}</td>
                    <td style={styles.td}>{couponCounts[partner.id] ?? 0}</td>
                    <td style={styles.td}>
                      <span style={styles.badge(partner.status === 'ACTIVE')}>
                        {partner.status === 'ACTIVE' ? 'Actif' : 'Désactivé'}
                      </span>
                    </td>
                    <td style={styles.td}>{formatDate(partner.created_at)}</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <Link to={`/admin/partners/${partner.id}`}>
                          <button style={styles.btnSecondary} data-testid={`partner-detail-btn-${partner.id}`}>
                            Détail
                          </button>
                        </Link>
                        <button
                          style={styles.btnSecondary}
                          onClick={() => openEdit(partner)}
                          data-testid={`partner-edit-btn-${partner.id}`}
                        >
                          ✏️
                        </button>
                        <button
                          style={partner.status === 'ACTIVE' ? styles.btnDanger : styles.btnSuccess}
                          onClick={() => handleToggleStatus(partner)}
                          data-testid={`partner-toggle-btn-${partner.id}`}
                        >
                          {partner.status === 'ACTIVE' ? 'Désactiver' : 'Activer'}
                        </button>
                        <button
                          style={styles.btnDanger}
                          onClick={() => handleDelete(partner)}
                          data-testid={`partner-delete-btn-${partner.id}`}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <PartnerModal
          title={editingPartner ? `Modifier "${editingPartner.name}"` : 'Nouveau partenaire'}
          form={form}
          onChange={setForm}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
          saving={saving}
          error={formError}
        />
      )}
    </div>
  )
}

export default AdminPartnersPage
